import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Button, Input } from '@/components/ui';
import { AuthHeading, FormMessage } from './AuthLayout';
import { PasswordField } from './PasswordField';
import { cognitoConfirmReset, cognitoRequestReset, friendlyAuthError } from './cognito';
import { isPasswordValid } from '@/lib/password';

export default function ForgotPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function request(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const clean = email.trim().toLowerCase();
    if (!clean) return setError('Enter your email address.');
    setBusy(true);
    try {
      await cognitoRequestReset(clean);
      setEmail(clean);
      setStep('confirm');
      setNotice(`If an account exists for ${clean}, a reset code is on its way.`);
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirm(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (code.trim().length < 4) return setError('Enter the code from your email.');
    if (!isPasswordValid(password)) return setError('Choose a password that meets all the requirements below.');
    setBusy(true);
    try {
      await cognitoConfirmReset(email, code, password);
      navigate('/signin', { replace: true, state: { email, reset: true } });
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {step === 'request' ? (
        <>
          <AuthHeading title="Reset your password" subtitle="Enter your email and we will send you a code." />
          <FormMessage error={error} />
          <form onSubmit={request} className="space-y-4" noValidate>
            <Input label="Email" type="email" name="email" autoComplete="email" inputMode="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            <Button type="submit" size="lg" className="w-full" loading={busy}>
              Send reset code
            </Button>
          </form>
        </>
      ) : (
        <>
          <AuthHeading title="Choose a new password" subtitle="Enter the code we emailed you and pick a new password." />
          <FormMessage error={error} notice={notice} />
          <form onSubmit={confirm} className="space-y-4" noValidate>
            <Input
              label="Reset code"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
              placeholder="123456"
              className="text-center font-mono text-xl tracking-[0.35em]"
            />
            <PasswordField label="New password" name="new-password" autoComplete="new-password" required showStrength value={password} onChange={(e) => setPassword(e.target.value)} />
            <Button type="submit" size="lg" className="w-full" loading={busy}>
              Update password
            </Button>
          </form>
        </>
      )}
      <p className="mt-6 text-center text-sm text-ink-muted">
        <Link to="/signin" className="font-medium text-link hover:underline">
          Back to sign in
        </Link>
      </p>
    </>
  );
}
